import { supabase } from '@/config/supabase'
import { fetchZoneQuotazioni, fetchCoefficientiExtra } from '@/services/zone-quotazioni.service'
import type { ValuationFormData, ValuationResult, AppliedCoefficient } from '@/types'

// Determina il prezzo base in base allo stato dell'immobile
function getPrezzoBase(
  prezzoAbitabile: number,
  prezzoRistrutturato: number,
  prezzoDaRistrutturare: number,
  condition?: string
): number {
  switch (condition) {
    case 'nuovo':
    case 'ristrutturato':
      return prezzoRistrutturato
    case 'da_ristrutturare':
      return prezzoDaRistrutturare
    case 'buono':
    default:
      return prezzoAbitabile
  }
}

// Calcola valutazione immobiliare
export async function calculateValuation(
  formData: ValuationFormData
): Promise<ValuationResult> {
  if (!formData.zoneName) {
    throw new Error('Zona non selezionata')
  }

  // 1. Carica dati zone e coefficienti da Supabase (con fallback hardcoded)
  const [zones, coefficientiExtra] = await Promise.all([
    fetchZoneQuotazioni(),
    fetchCoefficientiExtra(),
  ])

  const zona = zones.find((z) => z.nome === formData.zoneName)
  if (!zona) {
    throw new Error(`Zona "${formData.zoneName}" non trovata`)
  }

  // 2. Determina prezzo base da stato immobile
  const prezzoBase = getPrezzoBase(
    zona.prezzoAbitabile,
    zona.prezzoRistrutturato,
    zona.prezzoDaRistrutturare,
    formData.condition
  )

  // 3. Calcola coefficienti additivi
  const appliedCoefficients: AppliedCoefficient[] = []
  let totalMultiplier = 1

  // Vista mare (zona-specifico)
  if (formData.view && formData.view !== 'nessuna') {
    const bonus = formData.view === 'parziale'
      ? zona.vistaMareBonus / 2
      : zona.vistaMareBonus
    totalMultiplier += bonus
    appliedCoefficients.push({
      type: 'vista_mare',
      key: formData.view,
      value: bonus,
      description: formData.view === 'parziale' ? 'Vista mare parziale' : 'Vista mare panoramica',
    })
  }

  // Senza ascensore (zona-specifico, solo se no ascensore)
  if (!formData.hasElevator) {
    totalMultiplier += zona.senzaAscensore
    appliedCoefficients.push({
      type: 'ascensore',
      key: 'false',
      value: zona.senzaAscensore,
      description: 'Senza ascensore',
    })
  }

  // Pertinenza (zona-specifico)
  if (formData.hasPertinenza) {
    totalMultiplier += zona.pertinenzaBonus
    appliedCoefficients.push({
      type: 'pertinenza',
      key: 'true',
      value: zona.pertinenzaBonus,
      description: 'Con pertinenza (cantina, soffitta)',
    })
  }

  // Esposizione ottima
  if (formData.hasGoodExposure) {
    totalMultiplier += zona.esposizioneBonus
    appliedCoefficients.push({
      type: 'esposizione',
      key: 'true',
      value: zona.esposizioneBonus,
      description: 'Esposizione ottima',
    })
  }

  // Spazi esterni
  if (formData.hasExternalSpaces) {
    totalMultiplier += zona.spaziEsterniBonus
    appliedCoefficients.push({
      type: 'spazi_esterni',
      key: 'true',
      value: zona.spaziEsterniBonus,
      description: 'Spazi esterni (terrazzo, balcone, giardino)',
    })
  }

  // Garage/Box
  if (formData.hasGarage) {
    const garageBonus = formData.garageType === 'posto_auto'
      ? coefficientiExtra.postoAuto
      : coefficientiExtra.box
    totalMultiplier += garageBonus
    appliedCoefficients.push({
      type: 'box',
      key: formData.garageType || 'box',
      value: garageBonus,
      description: formData.garageType === 'posto_auto' ? 'Posto auto' : 'Box auto',
    })
  }

  // Piscina
  if (formData.hasPool) {
    totalMultiplier += coefficientiExtra.piscina
    appliedCoefficients.push({
      type: 'piscina',
      key: 'true',
      value: coefficientiExtra.piscina,
      description: 'Piscina',
    })
  }

  // Residence
  if (formData.isResidence) {
    totalMultiplier += coefficientiExtra.residence
    appliedCoefficients.push({
      type: 'residence',
      key: 'true',
      value: coefficientiExtra.residence,
      description: 'In residence / Condominio con servizi',
    })
  }

  // 4. Moltiplicatore tipo immobile (moltiplicativo, non additivo)
  let tipoMultiplier = 1
  if (formData.propertyType === 'villa') {
    tipoMultiplier = zona.villa
    appliedCoefficients.push({
      type: 'tipo_immobile',
      key: 'villa',
      value: zona.villa - 1,
      description: 'Tipo: Villa',
    })
  } else if (formData.propertyType === 'villetta') {
    tipoMultiplier = zona.casa
    appliedCoefficients.push({
      type: 'tipo_immobile',
      key: 'villetta',
      value: zona.casa - 1,
      description: 'Tipo: Casa / Villetta',
    })
  }

  // 5. Calcola prezzo finale
  const pricePerSqm = prezzoBase * totalMultiplier * tipoMultiplier
  const estimatedValue = pricePerSqm * formData.area

  // Range ±10%
  const minValue = estimatedValue * 0.90
  const maxValue = estimatedValue * 1.10

  return {
    estimatedValue: Math.round(estimatedValue),
    minValue: Math.round(minValue),
    maxValue: Math.round(maxValue),
    pricePerSqm: Math.round(pricePerSqm),
    zoneName: formData.zoneName,
    prezzoBaseMq: prezzoBase,
    appliedCoefficients,
    formData,
  }
}

// Salva valutazione su Supabase
export async function saveValuation(result: ValuationResult): Promise<string> {
  const { data, error } = await supabase
    .from('valuations_sanremo')
    .insert({
      address: result.formData.address,
      latitude: result.formData.latitude,
      longitude: result.formData.longitude,
      zone_name_csv: result.zoneName,
      property_type: result.formData.propertyType,
      area: result.formData.area,
      rooms: result.formData.rooms,
      bathrooms: result.formData.bathrooms,
      floor: result.formData.floor,
      has_elevator: result.formData.hasElevator,
      condition: result.formData.condition,
      view: result.formData.view,
      has_pertinenza: result.formData.hasPertinenza,
      has_good_exposure: result.formData.hasGoodExposure,
      has_external_spaces: result.formData.hasExternalSpaces,
      has_garage: result.formData.hasGarage,
      garage_type: result.formData.garageType,
      has_pool: result.formData.hasPool,
      is_residence: result.formData.isResidence,
      estimated_value: result.estimatedValue,
      min_value: result.minValue,
      max_value: result.maxValue,
      price_per_sqm: result.pricePerSqm,
      applied_coefficients: result.appliedCoefficients,
    })
    .select('id')
    .single()

  if (error) {
    console.error('Error saving valuation:', error)
    throw error
  }

  return data.id
}
